// One drawer per shop.
//
// The panel is a frame on somebody else's page, and a browser files what a
// frame saves under the address in the address bar — the shop's, not ours. Two
// brands served from one domain therefore share one drawer, which our own site
// does today with /tseppas and /atelierdelile. Under a single key the second
// brand opened the first brand's saved sign-in and talked to the wrong shop: a
// visitor on Atelier DeLile was answered with Tseppas's catalogue, because the
// restored token is tried before the checked one in App.js.
//
// Splitting on the shop rather than on the host address is deliberate. The same
// script runs on Shopify, on WooCommerce and on our own themes, and none of
// them agree on what a domain or a path means. The shop is the one thing we
// always know, because the brand embedded its key.
//
// Kept apart from the store itself so it can be tested without dragging the
// whole service layer in behind it.

import { getEmbeddedOrgId } from "../Utils/embedOrg";
import { tokenBelongsToOrg } from "../Utils/guestSession";

export const LEGACY_PERSIST_KEY = "MEETUSVR_CHAT_WIDGET";

// redux-persist's own prefix on the stored item. Named because the adoption
// below reads and writes that item directly, before a store exists to do it.
const PERSIST_ITEM_PREFIX = "persist:";

// Standing on its own, the app has no host page to share a drawer with, so it
// keeps the unsuffixed key it has always used.
export const persistKeyFor = (orgId) =>
  orgId ? `${LEGACY_PERSIST_KEY}:${orgId}` : LEGACY_PERSIST_KEY;

// Nobody is signed out by the renaming.
//
// On the first load under a shop's own key, a sign-in already saved under the
// old shared one is carried across when the token says it belongs to this
// shop. A token belonging to the other brand on the same domain is left
// exactly where it is — it is still theirs, and it is the case that caused the
// fault in the first place.
//
// Copied rather than moved: the brand it belongs to may not have been visited
// since the change, and would otherwise lose its session to a shop that was.
export const adoptLegacySession = (orgId) => {
  if (!orgId) return;
  try {
    const store = window.localStorage;
    if (!store) return;

    const target = PERSIST_ITEM_PREFIX + persistKeyFor(orgId);
    // This shop has its own drawer already; the old one is history.
    if (store.getItem(target)) return;

    const legacy = store.getItem(PERSIST_ITEM_PREFIX + LEGACY_PERSIST_KEY);
    if (!legacy) return;

    // redux-persist stores each slice as its own JSON string inside the item.
    const auth = JSON.parse(JSON.parse(legacy).auth ?? "null");
    if (!tokenBelongsToOrg(auth?.token, orgId)) return;

    store.setItem(target, legacy);
  } catch (e) {
    // A drawer we cannot read or write is one the visitor signs into again.
    // Never worth failing the app's start over.
  }
};

// The key this page load should persist under, with any inheritable session
// already carried across.
export const resolvePersistKey = () => {
  const orgId = getEmbeddedOrgId();
  adoptLegacySession(orgId);
  return persistKeyFor(orgId);
};
